import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { zIndex } from '@cbhq/cds-common/tokens/zIndex';
import { IconButton } from '@cbhq/cds-web/buttons';
import { Dropdown } from '@cbhq/cds-web/dropdown/Dropdown';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Box, Divider } from '@cbhq/cds-web/layout';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { ActionType, ComponentType } from '@cbhq/client-analytics';

import { useAnalytics } from '../ProfileMenu/hooks/useAnalytics';
import { useClientProps } from '../ProfileMenu/providers/ClientPropsProvider';
import { useProfileMenuV2 } from '../ProfileMenu/providers/ProfileMenuV2Provider';

import { HelpMenuDropdown } from './HelpMenuDropdown';

const messages = defineMessages({
  openHelpMenu: {
    defaultMessage: 'Help',
    id: 'HelpMenuContent.openHelpMenu',
    description: 'Accessibility label for the help menu button.',
  },
  closeHelpMenu: {
    defaultMessage: 'Close Help Menu',
    id: 'HelpMenuContent.closeHelpMenu',
    description: 'Accessibility label for the close help menu button.',
  },
});

const mobileContentWrapperStyle: CSSProperties = {
  scrollbarWidth: 'none',
};

export function HelpMenuContent() {
  const { logEvent } = useAnalytics();
  const { isPhone } = useBreakpoints();
  const { helpMenuRef } = useClientProps();
  const { helpMenu } = useProfileMenuV2();
  const { formatMessage } = useIntl();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const documentScrollDefaultRef = useRef(document.documentElement.style.overflow);

  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    if (!visible && document.documentElement.style.overflow !== documentScrollDefaultRef.current) {
      // Restore the default overflow style
      document.documentElement.style.overflow = documentScrollDefaultRef.current;
    } else if (isPhone && visible) {
      // Prevent background scrolling when the modal is open
      document.documentElement.style.overflow = 'hidden';
    }
  }, [isPhone, visible]);

  const handleOpen = useCallback(() => {
    logEvent('help_menu_open', {
      action: ActionType.click,
      componentType: ComponentType.button,
    });
  }, [logEvent]);

  const handleClose = useCallback(() => {
    buttonRef?.current?.focus();
    logEvent('help_menu_close', {
      action: ActionType.click,
      componentType: ComponentType.button,
    });
  }, [logEvent]);

  const handlePhonePress = useCallback(() => {
    if (visible) {
      handleClose();
      setVisible(false);
    } else {
      handleOpen();
      setVisible(true);
    }
  }, [visible, handleClose, handleOpen]);

  useEffect(() => {
    // Make the openMenu and closeMenu functions available for mobile
    if (isPhone && helpMenuRef?.current) {
      helpMenuRef.current.openMenu = handlePhonePress;
      helpMenuRef.current.closeMenu = handlePhonePress;
    }
  }, [isPhone, helpMenuRef, handlePhonePress]);

  if (!helpMenu) {
    return null;
  }

  if (isPhone && helpMenu.actions.length > 1) {
    return (
      <>
        <IconButton
          accessibilityLabel={formatMessage(messages.openHelpMenu)}
          name="unknown"
          variant="secondary"
          onClick={handlePhonePress}
        />
        {visible && (
          <VStack
            position="fixed"
            width="100vw"
            height="100vh"
            zIndex={zIndex.portal}
            left={0}
            top={0}
            ref={helpMenuRef as unknown as React.RefObject<HTMLDivElement>}
            key="help-dropdown"
            background="bg"
          >
            <HStack width="100%" paddingY={1} paddingX={2} justifyContent="flex-end">
              <IconButton
                accessibilityLabel={formatMessage(messages.closeHelpMenu)}
                name="close"
                color="fg"
                onClick={handlePhonePress}
                transparent
              />
            </HStack>
            <Divider />
            <Box overflow="scroll" padding={0} style={mobileContentWrapperStyle}>
              <HelpMenuDropdown />
            </Box>
          </VStack>
        )}
      </>
    );
  }
  const callbackUrl = helpMenu?.actions?.length ? helpMenu?.actions[0].callbackUrl : '';

  return helpMenu.actions.length > 1 ? (
    <Dropdown
      enableMobileModal
      onOpenMenu={handleOpen}
      onCloseMenu={handleClose}
      content={<HelpMenuDropdown />}
      ref={helpMenuRef}
      maxHeight="100%"
      showOverlay
    >
      <IconButton
        accessibilityLabel={formatMessage(messages.openHelpMenu)}
        name="unknown"
        variant="secondary"
      />
    </Dropdown>
  ) : (
    <IconButton
      accessibilityLabel={formatMessage(messages.openHelpMenu)}
      name="unknown"
      variant="secondary"
      href={callbackUrl}
      target="_blank"
      onClick={handleOpen}
      as="a"
    />
  );
}
